/**
 * App Switcher dropdown.
 *
 * Renders suite apps from Central's switch-context response.
 * No hardcoded app list — driven entirely by Central's apps[].
 * Current app (beheard) is highlighted with a checkmark and cannot be clicked.
 * Other apps navigate via window.location.assign(app.webUrl).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { selectSuiteAppsNormalized } from '../store/slices/centralAuthSlice';
import { APP_ID } from '../config/centralAuth';

function AppIcon({ iconUrl, displayName, className }: { iconUrl?: string | null; displayName: string; className?: string }) {
  const [imgFailed, setImgFailed] = useState(false);
  const handleError = useCallback(() => setImgFailed(true), []);

  if (iconUrl && !imgFailed) {
    return (
      <img
        src={iconUrl}
        alt={displayName}
        className={clsx('w-6 h-6 rounded flex-shrink-0 object-contain', className)}
        onError={handleError}
      />
    );
  }

  return (
    <span className={clsx('w-6 h-6 rounded bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0', className)}>
      {displayName[0]?.toUpperCase()}
    </span>
  );
}

export function AppSwitcher() {
  const apps = useSelector(selectSuiteAppsNormalized);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if only the current app (nothing to switch to)
  if (apps.length <= 1) {
    return null;
  }

  const handleAppClick = (webUrl: string) => {
    setIsOpen(false);
    window.location.assign(webUrl);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Switch app"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
            Apps
          </div>

          {apps.map((app) => {
            const isCurrent = app.id === APP_ID;
            return (
              <button
                key={app.id}
                type="button"
                onClick={() => !isCurrent && handleAppClick(app.webUrl)}
                disabled={isCurrent}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                  isCurrent
                    ? 'bg-primary-50 text-primary-700 cursor-default'
                    : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                )}
              >
                <AppIcon iconUrl={app.iconUrl} displayName={app.displayName} />
                <span className="flex-1 text-left truncate font-medium">{app.displayName}</span>
                {isCurrent && (
                  <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
